import {
  Autocomplete,
  Box,
  Checkbox,
  Chip,
  ListItem,
  TextField,
  Typography,
  TypographyProps,
} from "@mui/material";
import lodash from "lodash";
import { Option } from "../constants/common";
import Flex, { FlexProps } from "./Flex";
import { DEFAULT_FONTSIZE } from "../utils/utils";
import { Fragment } from "react";
import { inputLabel } from "./MaskInput";

export interface MultiSelectProps {
  name?: string;
  label?: string;
  required?: boolean;
  flexProps?: FlexProps;
  placeholder?: string;
  options: Option[];
  value?: string[];
  onChange?: (newOption: string[]) => void;
  error?: string;
  optionWithCheckbox?: boolean;
  optionWithSelectAll?: boolean;
  typographyProps?: TypographyProps;
  limitTags?: number;
  handleBlur?: any;
}

export const MultiSelect = ({
  name,
  label,
  required,
  flexProps,
  placeholder = "Select",
  options,
  error,
  value = [],
  onChange,
  optionWithSelectAll = false,
  optionWithCheckbox = false,
  limitTags = 1,
  handleBlur,
}: MultiSelectProps) => {
  const displayOptions =
    optionWithSelectAll && options?.length > 1
      ? [selectAllOption, ...options]
      : options;

  const handleChange = (
    _: any,
    newOptions: Option[],
    _reason: any,
    details: any
  ) => {
    const selectedValues = newOptions.map((option) => option.value);
    const isSelectAllClicked = details?.option?.value === "all";
    /* If "Select All" is clicked, toggle all selections
     */
    if (optionWithSelectAll) {
      if (isSelectAllClicked) {
        return handleSelectAll(value.length === options.length + 1);
      }

      /**
       * If all options are selected but "Select All" is not yet in value, add select all option
       */
      if (
        options.length === newOptions.length &&
        !selectedValues.includes(selectAllOption.value)
      ) {
        return onChange?.([...selectedValues, selectAllOption.value]);
      }

      /** If some options are being deselected, remove "Select All"*/
      if (value.includes(selectAllOption.value)) {
        onChange?.(
          selectedValues.filter((val) => val !== selectAllOption.value)
        );
        return;
      }
    }

    // Default change handler for Regular selection
    onChange?.(selectedValues);
  };

  const handleSelectAll = (should_deselect: boolean) =>
    onChange?.(
      should_deselect ? [] : [...options.map((opt) => opt.value), "all"]
    );

  return (
    <Flex
      {...flexProps}
      flexDirection={flexProps?.flexDirection ?? "column"}
      width={"100%"}
    >
      {/* {label && (
        <Typography mb={"10px"} sx={DEFAULT_FONTSIZE}>
          {label}
          {required && (
            <Typography component={"span"} color={"error"}>
              {` *`}
            </Typography>
          )}
        </Typography>
      )} */}
      <Autocomplete<Option, true>
        limitTags={limitTags}
        renderTags={(value, getTagProps) => {
          const numTags = value.length;
          return (
            <Fragment key={value + "id"}>
              {value.slice(0, limitTags).map((option, index) => (
                <Chip
                  size="small"
                  sx={{ height: "20px" }}
                  {...getTagProps({ index })}
                  key={index}
                  label={option?.label}
                />
              ))}

              {numTags > limitTags && (
                <Box
                  component="span"
                  sx={{
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    fontSize: "13px",
                    ml: 0.25,
                  }}
                >
                  {`+${numTags - limitTags}`}
                </Box>
              )}
            </Fragment>
          );
        }}
        options={displayOptions ?? []}
        multiple
        fullWidth
        disableCloseOnSelect
        getOptionLabel={(option) => option.label}
        sx={{
          "& .MuiInputBase-input": DEFAULT_FONTSIZE,
          // Keep selected chips + counter on a single line within the
          // fixed-height input so they never overflow onto the label.
          "& .MuiOutlinedInput-root": { flexWrap: "nowrap" },
          // Let the chip shrink (ellipsis) to fit the leftover space
          // instead of pushing the counter/input out of bounds.
          "& .MuiChip-root": { minWidth: 0 },
        }}
        isOptionEqualToValue={(option, value) => lodash.isEqual(option, value)}
        value={displayOptions.filter(
          (option) => option?.value && value?.includes(option.value)
        )}
        onChange={handleChange}
        renderOption={(props, option, { selected }) => (
          <ListItem key={option.value} {...props} sx={DEFAULT_FONTSIZE}>
            {optionWithCheckbox && (
              <Checkbox
                style={{ padding: 0, marginRight: "6px" }}
                checked={selected}
              />
            )}
            {option.label}
          </ListItem>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            name={name}
            onBlur={handleBlur}
            label={inputLabel(label, required)}
            InputLabelProps={{ shrink: true }}
            error={!!error}
            placeholder={value && value?.length === 0 ? placeholder : undefined}
          />
        )}
      />
      {!!error && (
        <Typography mt={"4px"} variant="caption" color={"error"}>
          {error}
        </Typography>
      )}
    </Flex>
  );
};

export default MultiSelect;

const selectAllOption: Option = { label: "All", value: "all" };
